import React, { useState, useEffect } from "react";
import { Search, LogOut, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import "../Styles/collaborateList.css";

const API_BASE_URL = "";

export default function CollaborateList() {
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [query, setQuery] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Bulk delete selection
  const [selected, setSelected] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const fetchCollaborators = async () => {
    try {
      setLoading(true);
      // The Collaborate form saves into the "partners" table
      const res = await fetch(`${API_BASE_URL}/api/partners?limit=100000`);
      if (!res.ok) throw new Error("Failed to load collaborators");
      const data = await res.json();
      setCollaborators(Array.isArray(data) ? data : data.partners || []);
      setError(null);
    } catch (err) {
      console.error(err);
      setCollaborators([]);
      setError("Could not load collaborators. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollaborators();
  }, []);

  const handleDelete = async (id, companyName) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete "${companyName}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e0432c",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "No, cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/partners/${id}`, {
        method: "DELETE",
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || data.error || "Failed to delete collaborator");
      }

      setCollaborators((prev) => prev.filter((c) => c.id !== id));
      setSelected((prev) => prev.filter((x) => x !== id));

      Swal.fire({
        title: "Deleted!",
        text: `"${companyName}" has been deleted.`,
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err.message || "Something went wrong. Please try again.",
        icon: "error",
      });
    }
  };

  // ---- Bulk delete ----
  const handleBulkDelete = async () => {
    if (selected.length === 0) {
      Swal.fire({
        icon: "info",
        title: "No Collaborators Selected",
        text: "Please select at least one collaborator to delete.",
      });
      return;
    }

    const result = await Swal.fire({
      title: `Delete ${selected.length} Collaborator${selected.length > 1 ? "s" : ""}?`,
      text: "Are you sure you want to delete all selected collaborators? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e0432c",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete All",
      cancelButtonText: "No",
    });
    if (!result.isConfirmed) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/partners/bulk-delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: selected }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || "Failed to delete");

      setCollaborators((prev) => prev.filter((c) => !selected.includes(c.id)));
      setSelected([]);
      setSelectAll(false);

      Swal.fire({
        title: "Deleted!",
        text: "The selected collaborators have been deleted.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err.message || "Failed to delete selected collaborators.",
        icon: "error",
      });
    }
  };

  const filtered = collaborators.filter((c) => {
    const q = query.toLowerCase().trim();
    if (!q) return true;
    return [
      c.fullName,
      c.designation,
      c.companyName,
      c.officialEmail,
      c.contactNumber,
      c.website,
      c.partnershipType,
      c.collaborateOn,
    ]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase().includes(q));
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [query, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const paginated = filtered.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Clear the selection when the visible page / search / page size changes
  useEffect(() => {
    setSelected([]);
    setSelectAll(false);
  }, [currentPage, query, pageSize]);

  const toggleAll = () => {
    if (selectAll) {
      setSelected([]);
      setSelectAll(false);
    } else {
      setSelected(paginated.map((c) => c.id));
      setSelectAll(true);
    }
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      setSelectAll(paginated.length > 0 && next.length === paginated.length);
      return next;
    });
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);
    if (start > 2) pages.push("dots-start");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push("dots-end");
    pages.push(totalPages);
    return pages;
  };

  const total = collaborators.length;

  const handleExportExcel = () => {
    if (filtered.length === 0) {
      Swal.fire({
        title: "No Records",
        text: "There are no collaborators to export.",
        icon: "info",
        timer: 1500,
        showConfirmButton: false,
      });
      return;
    }

    const excelRows = filtered.map((c, index) => ({
      "S.No": index + 1,
      "Full Name": c.fullName || "",
      "Designation": c.designation || "",
      "Company Name": c.companyName || "",
      "Official Email": c.officialEmail || "",
      "Contact Number": c.contactNumber || "",
      "Website": c.website || "",
      "Partnership Type": c.partnershipType || "",
      "Collaborate On": c.collaborateOn || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelRows);

    worksheet["!cols"] = [
      { wch: 8 },
      { wch: 26 },
      { wch: 26 },
      { wch: 28 },
      { wch: 32 },
      { wch: 18 },
      { wch: 32 },
      { wch: 20 },
      { wch: 50 },
    ];

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Collaborators");

    XLSX.writeFile(workbook, "collaborators.xlsx");

    Swal.fire({
      title: "Exported!",
      text: "Collaborators Excel file downloaded successfully.",
      icon: "success",
      timer: 1500,
      showConfirmButton: false,
    });
  };

  return (
    <div className="col-app">
      {/* ---------------- Main content ---------------- */}
      <main className="col-main">
        <section className="col-panel">
          <div className="col-controls">
            <label className="col-show-entries">
              Show Entries :
              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>

            <span className="col-pill col-pill--total">Total Collaborators : {total}</span>

            <button
              className="col-pill col-pill--export"
              onClick={handleExportExcel}
            >
              <span className="col-excel-icon-wrap">
                <img
                  src="/assets/excel.png"
                  alt="Excel"
                  className="col-excel-icon col-excel-icon--default"
                />
                <img
                  src="/assets/excel-white.png"
                  alt="Excel"
                  className="col-excel-icon col-excel-icon--hover"
                />
              </span>
              Export To Excel
            </button>

            <div className="col-search">
              <Search size={14} />
              <input
                placeholder="Search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Bulk delete - left end, below Show Entries */}
          <div
            className="col-bulk-row"
            style={{ display: "flex", justifyContent: "flex-start" }}
          >
            <button className="col-pill col-pill--bulk" onClick={handleBulkDelete}>
              <Trash2 size={14} />
              Bulk Delete{selected.length > 0 ? ` (${selected.length})` : ""}
            </button>
          </div>

          <div className="col-table-outer">
            <div className="col-table-inner">
              {loading ? (
                <p style={{ padding: "20px" }}>Loading collaborators...</p>
              ) : error ? (
                <p style={{ padding: "20px", color: "#e0432c" }}>{error}</p>
              ) : (
                <table className="col-table">
                  <thead>
                    <tr>
                      <th className="col-checkbox-col">
                        <label className="col-select-all">
                          <input type="checkbox" checked={selectAll} onChange={toggleAll} />
                          <span>Select All</span>
                        </label>
                      </th>
                      <th>S.No</th>
                      <th>Full Name</th>
                      <th>Designation</th>
                      <th>Company Name</th>
                      <th>Official Email</th>
                      <th>Contact Number</th>
                      <th>Website</th>
                      <th>Partnership Type</th>
                      <th>Collaborate On</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.length === 0 ? (
                      <tr>
                        <td colSpan={11} style={{ padding: "20px", textAlign: "center" }}>
                          No collaborators yet.
                        </td>
                      </tr>
                    ) : (
                      paginated.map((c, index) => (
                        <tr key={c.id}>
                          <td className="col-checkbox-col">
                            <input
                              type="checkbox"
                              checked={selected.includes(c.id)}
                              onChange={() => toggleOne(c.id)}
                            />
                          </td>
                          <td>{(currentPage - 1) * pageSize + index + 1}</td>
                          <td>{c.fullName}</td>
                          <td>{c.designation}</td>
                          <td>{c.companyName}</td>
                          <td>{c.officialEmail}</td>
                          <td>{c.contactNumber}</td>
                          <td>
                            {c.website ? (
                              <a
                                href={/^https?:\/\//i.test(c.website) ? c.website : `https://${c.website}`}
                                target="_blank"
                                rel="noreferrer"
                                className="col-link"
                              >
                                View Website
                              </a>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td>{c.partnershipType}</td>
                          <td>{c.collaborateOn}</td>
                          <td>
                            <button
                              className="col-delete-btn"
                              onClick={() => handleDelete(c.id, c.companyName)}
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="col-table-footer">
            <span>
              Showing {paginated.length === 0 ? 0 : (currentPage - 1) * pageSize + 1}
              &ndash;
              {(currentPage - 1) * pageSize + paginated.length} of {filtered.length} Members
            </span>
            <div className="col-pagination">
              <button
                className="col-page-btn"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={14} />
              </button>
              {getPageNumbers().map((p, i) =>
                p === "dots-start" || p === "dots-end" ? (
                  <span key={`${p}-${i}`} className="col-page-dots">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    className={`col-page-btn${p === currentPage ? " active" : ""}`}
                    onClick={() => goToPage(p)}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                className="col-page-btn"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}